import React, { useState } from 'react';

// Styles & Assets
import styles from './DialogNewAssociatedUser.module.css';

// Services 
import { validateNonEmpty } from '../../../services/General';
import { getEnvironmentApiUrl } from '../../../services/General';

// Hooks
import { useRef } from 'react';
import useHttp from '../../../hooks/useHttp';
import useInput from '../../../hooks/useInput';
import { useToast } from '../../../hooks/useToast';

// Components
import Input from '../../UI/Input/Input';
import InputSelect from '../../UI/InputSelect/InputSelect';

// Language
import { useTranslation } from 'react-i18next';
import CheckboxButton from '../../UI/CheckboxButton/CheckboxButton';

// Interfaces
import { Carer, patientCarerWithCarer } from '../../../interfaces/Carer';
import { InputHook, OptionsSelect } from '../../../interfaces/Input';

interface IInput {
    input: InputHook
    label: string
    required?: true
    select?: true
}

const DialogNewAssociatedUser: React.FC<{
    setShowModal: React.Dispatch<React.SetStateAction<boolean>>
    patientCarer: patientCarerWithCarer | null,
    patient_id: number,
    setCarer: (carer: patientCarerWithCarer, addNew: boolean) => void
    mainCarer: patientCarerWithCarer | undefined
}> = ({ setShowModal, patientCarer, patient_id, setCarer, mainCarer }) => {

    const { t } = useTranslation();

    const toast = useToast();

    const inputs: IInput[] = [
        { input: useInput(validateNonEmpty, patientCarer?.carer?.first_name, t('field_required') as string), label: t('name'), required: true },
        { input: useInput(undefined, patientCarer?.carer?.last_name_1), label: t('last_name1') },
        { input: useInput(undefined, patientCarer?.carer?.last_name_2 || ''), label: t('last_name2') },
        { input: useInput(undefined, patientCarer?.carer?.mobile_phone || ''), label: t('mobile_phone') },
        { input: useInput(undefined, patientCarer?.carer?.home_phone || ''), label: t('phone') },
        { input: useInput(undefined, patientCarer?.carer?.email || ''), label: t('email') },
        { input: useInput(undefined, patientCarer?.carer?.nid ?? ''), label: t('DNI') },
        {
            input: useInput(undefined, (patientCarer?.is_formal_carer ? t('formal_carer') : t('informal_carer')) as string),
            label: t('role'), select: true
        },
    ];

    const [isCohabiting, setIsCohabiting] = useState(patientCarer?.is_cohabiting);
    const isMainCarer = useRef(patientCarer?.is_main_carer);
    const commentRef = useRef<HTMLTextAreaElement | null>(null);

    const { sendRequest } = useHttp<Carer>();
    const { sendRequest: assignCarer } = useHttp();

    const handleSetCarer = (patientCarer: patientCarerWithCarer, addNew: boolean) => {
        setCarer(patientCarer, addNew);
        setShowModal(false);
    };

    const sendNewCarerRole = (data: Carer | Error, addNew: boolean) => {
        if (data instanceof Error) {
            return;
        }

        const patientCarerInfo = {
            carer_id: data.id,
            is_formal_carer: inputs[7].input.value === t('formal_carer'),
            is_cohabiting: isCohabiting,
            is_main_carer: isMainCarer.current,
            patient_id: patient_id,
        };

        assignCarer({
            url: getEnvironmentApiUrl() + 'patients_carer/',
            method: patientCarer === null ? 'POST' : 'PUT',
            body: patientCarerInfo,
        }, () => {
            handleSetCarer({ carer: data, ...patientCarerInfo }, addNew);
        }, () => toast.error(`${t('error_adding')} ${t('carer')}`));

        if (mainCarer && isMainCarer.current === true && mainCarer.carer_id !== patientCarer?.carer_id) {
            assignCarer({
                url: getEnvironmentApiUrl() + 'patients_carer/',
                method: 'PUT',
                body: {
                    carer_id: mainCarer.carer_id,
                    is_cohabiting: mainCarer.is_cohabiting,
                    is_formal_carer: mainCarer.is_formal_carer,
                    is_main_carer: false,
                    patient_id: patient_id,
                },
            }, () => {
                handleSetCarer({ ...mainCarer, is_main_carer: false }, false);
            }, () => toast.error(`${t('error_adding')} ${t('carer')}`));
        }
    };

    const handleConfirm = (e: React.FormEvent) => {

        e.preventDefault();

        const body = {
            first_name: inputs[0].input.value.trim(),
            last_name_1: inputs[1].input.value.trim(),
            last_name_2: inputs[2].input.value.trim(),
            mobile_phone: inputs[3].input.value,
            home_phone: inputs[4].input.value,
            email: inputs[5].input.value,
            nid: inputs[6].input.value.trim() === '' ? null : inputs[6].input.value,
            comment: commentRef.current?.value ?? '',
            middle_name: null,
        };

        if (inputs.some(input => !input.input.valueIsValid)) {
            inputs.forEach(input => input.input.inputBlurHandler());
            return;
        }

        if (patientCarer !== null && patientCarer.carer !== undefined) {
            const patientCarerCarer = patientCarer.carer;
            sendRequest({
                url: getEnvironmentApiUrl() + `carer/${patientCarerCarer.id}`,
                method: 'PUT',
                body
            }, () => {
                if (patientCarer.is_formal_carer !== (inputs[7].input.value === t('formal_carer')) ||
                    (isMainCarer.current !== patientCarer.is_main_carer && patientCarerCarer) ||
                    (isCohabiting !== patientCarer.is_cohabiting))
                    sendNewCarerRole({ ...patientCarerCarer, ...body }, false);
                else handleSetCarer({ ...patientCarer, carer: { ...patientCarerCarer, ...body } }, false);
            }, () => toast.error(`${t('error_adding')} ${t('carer')}`));
        }
        else sendRequest({
            url: getEnvironmentApiUrl() + 'carer/',
            method: 'POST',
            body
        }, (carer) => sendNewCarerRole(carer, true), () => {
            toast.error(`${t('error_adding')} ${t('carer')}`);
        });
    };

    const handleClickMainCarer = (checked: boolean) => isMainCarer.current = checked;

    const optionsCarer = [{ id: 0, name: t('formal_carer') }, { id: 1, name: t('informal_carer') }];

    return (
        <form className='dialog' onSubmit={handleConfirm}>
            <div className={styles.titleContainer}>
                <h1 className='dialogTitle'>{t('new_associated_user')}</h1>
                <CheckboxButton
                    label={t('main_carer')}
                    defaultChecked={patientCarer?.is_main_carer}
                    onClick={handleClickMainCarer}
                />
            </div>
            <div className={styles.formInputs}>
                {inputs.map((input, index) => !input.select ?
                    <Input
                        key={index}
                        label={input.label}
                        defaultValue={input.input.value}
                        error={input.required ? input.input.hasError : undefined}
                        errorMsg={input.required ? t(input.input.errorMsg) : undefined}
                        required={input.required}
                        onBlur={input.required ? input.input.inputBlurHandler : undefined}
                        onChange={input.input.valueChangeHandler}
                    />
                    :
                    <InputSelect
                        key={index}
                        options={optionsCarer}
                        value={input.input.value}
                        label={input.label}
                        onChange={(option: OptionsSelect) => input.input.valueChangeHandler(undefined, option.name)}
                    />
                )}
                <InputSelect
                    options={[{ id: 0, name: t('yes') }, { id: 1, name: t('no') }]}
                    label={t('living_with_patient')}
                    onChange={(option: OptionsSelect) => setIsCohabiting(option.id === 0)}
                    value={isCohabiting ? t('yes') : t('no')}
                />
            </div>
            <textarea
                placeholder={t('comment') as string}
                defaultValue={patientCarer?.carer?.comment ?? ''}
                className={styles.textArea}
                ref={commentRef}
            />

            <div className={styles.buttonsContainer}>
                <button type='button' onClick={() => setShowModal(false)} className='button'>{t('cancel')}</button>
                <button type='submit' className='button'>{t('confirm')}</button>
            </div>
        </form>
    );
};

export default DialogNewAssociatedUser;